import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  axios.defaults.baseURL = API_URL;

  // Set auth header on all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is logged in on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password, recaptchaToken) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
        recaptchaToken,
      });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(newToken);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed. Please try again.',
        needsVerification: error.response?.data?.needsVerification,
      };
    }
  };

  const signupAirlinesAdmin = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/signup/airlines-admin`, userData);
      return {
        success: true,
        message: response.data.message,
        autoVerified: response.data.autoVerified,
        needsVerification: response.data.needsVerification,
        email: response.data.email,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Signup failed. Please try again.',
        needsVerification: error.response?.data?.needsVerification,
        email: error.response?.data?.email,
      };
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      const response = await axios.post(`${API_URL}/auth/resend-verification`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend verification email.',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    user,
    token,
    loading,
    login,
    signupAirlinesAdmin,
    resendVerificationEmail,
    logout,
    updateUser,
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
